import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const SystemStatistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const { data, error } = await supabase?.from('admin_statistics')?.select('*')?.single();

      if (!error) {
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  const completionRate = stats?.total_deals > 0 
    ? ((stats?.completed_deals / stats?.total_deals) * 100)?.toFixed(1)
    : 0;

  const verificationRate = stats?.total_users > 0
    ? ((stats?.verified_users / stats?.total_users) * 100)?.toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">System Statistics</h2>
        <p className="text-muted-foreground">Comprehensive platform analytics and insights</p>
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={24} className="text-blue-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-foreground mb-1">{stats?.total_users || 0}</p>
          <p className="text-sm text-muted-foreground">Total Users</p>
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Buyers</span>
              <span className="text-foreground font-medium">{stats?.total_buyers || 0}</span>
            </div>
            <div className="flex items-center justify-between text-xs mt-2">
              <span className="text-muted-foreground">Travelers</span>
              <span className="text-foreground font-medium">{stats?.total_travelers || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
              <Icon name="ShoppingBag" size={24} className="text-green-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-foreground mb-1">{stats?.total_requests || 0}</p>
          <p className="text-sm text-muted-foreground">Purchase Requests</p>
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Active</span>
              <span className="text-green-600 font-medium">{stats?.active_requests || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Package" size={24} className="text-purple-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-foreground mb-1">{stats?.total_deals || 0}</p>
          <p className="text-sm text-muted-foreground">Total Deals</p>
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Completion Rate</span>
              <span className="text-purple-600 font-medium">{completionRate}%</span>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={24} className="text-amber-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-foreground mb-1">${(stats?.total_revenue || 0)?.toLocaleString()}</p>
          <p className="text-sm text-muted-foreground">Total Revenue</p>
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Deals Today</span>
              <span className="text-amber-600 font-medium">{stats?.deals_today || 0}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Platform Performance</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Deal Completion Rate</span>
                <span className="text-sm font-semibold text-foreground">{completionRate}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full" style={{ width: `${completionRate}%` }}></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">User Verification Rate</span>
                <span className="text-sm font-semibold text-foreground">{verificationRate}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${verificationRate}%` }}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-500/10 rounded-full flex items-center justify-center">
                <Icon name="TrendingUp" size={16} className="text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Active Growth</p>
                <p className="text-xs text-muted-foreground">{stats?.deals_today || 0} new deals today</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-500/10 rounded-full flex items-center justify-center">
                <Icon name="Users" size={16} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">User Engagement</p>
                <p className="text-xs text-muted-foreground">{stats?.active_requests || 0} active requests</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemStatistics;